import React, { useState, useRef, useEffect } from "react";
import Button from "../../common/Button/Button";
import {
  uploadImage,
  modifyPlanImages,
  modifyPlanCountryImages,
} from "../../service/planCountryImageService";
import Loader from "../../common/Loader/Loader";
import ImageCropper from "../../common/ImageCropper/ImageCropper";

const AddImageModal = ({
  isOpen,
  onClose,
  plans,
  eventTypeId,
  get: _get,
  post,
  showToast,
  onSuccess,
}) => {
  const [originalFile, setOriginalFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageName, setImageName] = useState("");
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const [isNoPlanSelected, setIsNoPlanSelected] = useState(false);
  const [uploadStep, setUploadStep] = useState(0); // 0: not started, 1: uploading file, 2: saving image, 3: linking plan
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setOriginalFile(null);
      setSelectedFile(null);
      setImagePreview(null);
      setShowCropper(false);
      setImageName("");
      setSelectedPlanIds([]);
      setIsNoPlanSelected(false);
      setUploadStep(0);
      setIsUploading(false);
    }
  }, [isOpen]);

  // Handle outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file.", "error");
      return;
    }

    // Set original file and show cropper
    setOriginalFile(file);
    setShowCropper(true);
  };

  const handleCropComplete = (croppedFile) => {
    setSelectedFile(croppedFile);
    setShowCropper(false);
    
    // Create preview from cropped file
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      const sizeMB = (croppedFile.size / 1024 / 1024).toFixed(2);
      showToast(`Image cropped and compressed to ${sizeMB}MB`, "success");
    };
    reader.readAsDataURL(croppedFile);
  };

  const handleCropCancel = () => {
    setOriginalFile(null);
    setShowCropper(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast("Please select an image file.", "error");
      return;
    }

    if (!imageName.trim()) {
      showToast("Please enter an image name.", "error");
      return;
    }

    setIsUploading(true);
    setUploadStep(1);

    try {
      // Step 1: Upload file
      const uploadResult = await uploadImage(post, selectedFile);
      if (!uploadResult.success) {
        showToast(uploadResult.message || "Failed to upload image.", "error");
        setIsUploading(false);
        setUploadStep(0);
        return;
      }

      const blobUrl = uploadResult.data?.blob_url;
      if (!blobUrl) {
        showToast("Upload successful but no URL returned.", "error");
        setIsUploading(false);
        setUploadStep(0);
        return;
      }

      setUploadStep(2);

      // Step 2: Save image URL and name
      const saveImageResult = await modifyPlanImages(post, eventTypeId, {
        plan_images: [
          {
            operation: "ADD",
            img_url: blobUrl,
            img_name: imageName.trim(),
          },
        ],
      });

      if (!saveImageResult.success) {
        showToast(
          saveImageResult.message || "Failed to save image details.",
          "error"
        );
        setIsUploading(false);
        setUploadStep(0);
        return;
      }

      const imageId = saveImageResult.data?.data?.[0]?.id;
      if (!imageId) {
        showToast("Image saved but no ID returned.", "error");
        setIsUploading(false);
        setUploadStep(0);
        return;
      }

      setUploadStep(3);

      // Step 3: Link image with plans (multiple plans or no plan)
      const planCountryImages = [];
      
      // Add "No Plan" association if selected
      if (isNoPlanSelected) {
        planCountryImages.push({
          operation: "ADD",
          plan_image_id: imageId,
          plan_id: null,
        });
      }
      
      // Add associations for each selected plan
      selectedPlanIds.forEach((planId) => {
        planCountryImages.push({
          operation: "ADD",
          plan_image_id: imageId,
          plan_id: planId,
        });
      });
      
      // If nothing selected, default to "No Plan"
      if (planCountryImages.length === 0) {
        planCountryImages.push({
          operation: "ADD",
          plan_image_id: imageId,
          plan_id: null,
        });
      }

      const linkResult = await modifyPlanCountryImages(post, eventTypeId, {
        plan_country_images: planCountryImages,
      });

      if (!linkResult.success) {
        showToast(
          linkResult.message || "Failed to link image with plan(s).",
          "error"
        );
        setIsUploading(false);
        setUploadStep(0);
        return;
      }

      // Success!
      showToast("Image uploaded and linked successfully!");
      onSuccess();
    } catch (error) {
      console.error("Upload error:", error);
      showToast("An unexpected error occurred. Please try again.", "error");
      setIsUploading(false);
      setUploadStep(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Add New Image</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            disabled={isUploading}
          >
            ×
          </button>
        </div>

        {isUploading ? (
          <div className="py-8">
            <Loader />
            <div className="mt-4 text-center">
              <p className="text-gray-600 mb-2">
                {uploadStep === 1 && "Uploading image file..."}
                {uploadStep === 2 && "Saving image details..."}
                {uploadStep === 3 && "Linking image with plan..."}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-orange-primary h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadStep / 3) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        ) : showCropper ? (
          <>
            {/* Image Cropper */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-4">Crop Image (16:9)</h3>
              <ImageCropper
                imageFile={originalFile}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
                aspectRatio={16 / 9}
                maxSizeMB={1}
                showCompression={true}
              />
            </div>
          </>
        ) : (
          <>
            {/* File Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Image (Will be cropped to 16:9)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-primary file:text-white hover:file:bg-orange-600"
                disabled={isUploading}
              />
            </div>

            {/* Image Preview */}
            {imagePreview && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="absolute top-0 left-0 w-full h-full object-cover rounded-lg border border-gray-300"
                  />
                </div>
              </div>
            )}

            {/* Image Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Name
              </label>
              <input
                type="text"
                value={imageName}
                onChange={(e) => setImageName(e.target.value)}
                placeholder="Enter image name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-primary"
                disabled={isUploading}
              />
            </div>

            {/* Plan Selection - Multi-select */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plans (Optional - Select multiple including "No Plan")
              </label>
              <div className="border border-gray-300 rounded-lg p-2 max-h-48 overflow-y-auto">
                {/* No Plan Option */}
                <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-200 mb-1">
                  <input
                    type="checkbox"
                    checked={isNoPlanSelected}
                    onChange={(e) => setIsNoPlanSelected(e.target.checked)}
                    className="w-4 h-4 text-orange-primary border-gray-300 rounded focus:ring-orange-primary"
                    disabled={isUploading}
                  />
                  <span className="text-sm text-gray-700 font-medium">
                    No Plan
                  </span>
                </label>

                {/* Regular Plans */}
                {plans.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2 px-2">
                    No plans available from API.
                  </p>
                ) : (
                  <>
                    {plans.map((plan) => (
                      <label
                        key={plan.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPlanIds.includes(plan.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPlanIds([...selectedPlanIds, plan.id]);
                            } else {
                              setSelectedPlanIds(
                                selectedPlanIds.filter((id) => id !== plan.id)
                              );
                            }
                          }}
                          className="w-4 h-4 text-orange-primary border-gray-300 rounded focus:ring-orange-primary"
                          disabled={isUploading}
                        />
                        <span className="text-sm text-gray-700">
                          {plan.plan_name}
                        </span>
                      </label>
                    ))}
                  </>
                )}
              </div>
              {(selectedPlanIds.length > 0 || isNoPlanSelected) && (
                <p className="text-xs text-gray-500 mt-1">
                  {isNoPlanSelected && selectedPlanIds.length > 0
                    ? `"No Plan" + ${selectedPlanIds.length} plan(s) selected`
                    : isNoPlanSelected
                    ? '"No Plan" selected'
                    : `${selectedPlanIds.length} plan(s) selected`}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                text="Cancel"
                onClick={onClose}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
                disabled={isUploading}
              />
              <Button
                text="Upload"
                onClick={handleUpload}
                className="bg-orange-primary text-white px-4 py-2 rounded"
                disabled={isUploading || !selectedFile || !imageName.trim()}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddImageModal;

